/**
 * The driver cards' working copy (FLEE-442 W3).
 *
 * Drivers live only in React state: never in localStorage, the console,
 * analytics or a URL. They leave the browser once, in the POST /soft-holds body.
 */
import type { RenterDriverFieldsInput } from "@fm/api";
import {
  REQUIRED_DRIVER_FIELDS,
  licenseValidThrough,
  missingFields,
  type RequiredDriverField,
} from "../../lib/renter-drivers";

export type DriverFormKey =
  | RequiredDriverField
  | "gender"
  | "license_street_2"
  | "email"
  | "phone";

export type DriverFormFields = Record<DriverFormKey, string>;

export type DriverFieldErrors = Partial<Record<DriverFormKey, string>>;

export interface DriverDraft {
  key: string;
  fields: DriverFormFields;
  /** The renter pressed Save driver and the client checks passed. */
  saved: boolean;
}

export type DriverCardStatus = "needed" | "in_progress" | "ready";

export const DRIVER_FORM_KEYS: readonly DriverFormKey[] = [
  ...REQUIRED_DRIVER_FIELDS,
  "gender",
  "license_street_2",
  "email",
  "phone",
];

/**
 * What an insurer that checks every driver needs to reach them: it sends each
 * one a link to verify their identity (FLEE-561).
 */
export const CONTACT_KEYS = ["email", "phone"] as const;

export type ContactKey = (typeof CONTACT_KEYS)[number];

/** Country is a select, so it starts on the fleet's most likely answer. */
export const DEFAULT_COUNTRY = "US";

export const COUNTRIES: readonly { value: string; label: string }[] = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
];

export const GENDERS = [
  "Female",
  "Male",
  "Non-binary",
  "Prefer not to say",
] as const;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

let nextKey = 0;

export function emptyFields(): DriverFormFields {
  const fields = {} as DriverFormFields;
  for (const key of DRIVER_FORM_KEYS) fields[key] = "";
  fields.license_country = DEFAULT_COUNTRY;
  return fields;
}

export function newDraft(): DriverDraft {
  nextKey += 1;
  return { key: `driver-${nextKey}`, fields: emptyFields(), saved: false };
}

/** `YYYY-MM-DD` of the browser's today, with no UTC shift. */
export function todayIso(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** "Dec 5, 2026" from a `YYYY-MM-DD` string, without `new Date(str)`. */
export function formatDay(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  const label = MONTHS[Number(month) - 1];
  if (!year || !label || !day) return iso;

  return `${label} ${Number(day)}, ${year}`;
}

const isBlank = (value: string) => value.trim() === "";

/** Anything typed in a field the renter chose, ignoring the country default. */
function hasInput(fields: DriverFormFields): boolean {
  return DRIVER_FORM_KEYS.some(
    (key) =>
      !isBlank(fields[key]) &&
      !(key === "license_country" && fields[key] === DEFAULT_COUNTRY),
  );
}

/** The message under the expiration date, or "" when the license is fine. */
export function expiryMessage(
  expiresOn: string,
  returnDate: string,
  today: string = todayIso(),
): string {
  if (isBlank(expiresOn)) return "";
  if (licenseValidThrough(expiresOn, returnDate)) return "";

  if (expiresOn.slice(0, 10) < today) {
    return `This license expired on ${formatDay(expiresOn)}. Use a current license.`;
  }

  return `This license expires on ${formatDay(expiresOn)}, before the return on ${formatDay(returnDate)}. Use a current license or add another driver.`;
}

/**
 * Required fields, the live expiry rule and a past date of birth. Optional
 * fields are never an error, unless `contact` names them. The server stays
 * the authority.
 */
export function validateDraft(
  fields: DriverFormFields,
  returnDate: string,
  today: string = todayIso(),
  contact: readonly ContactKey[] = [],
): DriverFieldErrors {
  const errors: DriverFieldErrors = {};

  for (const key of missingFields(fields)) errors[key] = "Required.";

  for (const key of contact) {
    if (isBlank(fields[key])) errors[key] = "Required.";
  }

  const expiry = expiryMessage(fields.license_expires_on, returnDate, today);
  if (expiry) errors.license_expires_on = expiry;

  const dob = fields.date_of_birth;
  if (!isBlank(dob) && dob.slice(0, 10) >= today) {
    errors.date_of_birth = "Enter a date of birth in the past.";
  }

  return errors;
}

/** Ready = saved, complete and valid through the return. */
export function isDraftReady(
  draft: DriverDraft,
  returnDate: string,
  contact: readonly ContactKey[] = [],
): boolean {
  return (
    draft.saved &&
    missingFields(draft.fields).length === 0 &&
    licenseValidThrough(draft.fields.license_expires_on, returnDate) &&
    contact.every((key) => !isBlank(draft.fields[key]))
  );
}

export function draftStatus(
  draft: DriverDraft,
  returnDate: string,
  contact: readonly ContactKey[] = [],
): DriverCardStatus {
  if (isDraftReady(draft, returnDate, contact)) return "ready";

  return hasInput(draft.fields) ? "in_progress" : "needed";
}

/** The last four characters of the license number, spaces ignored. */
export function licenseLast4(number: string): string {
  return number.replace(/\s/g, "").slice(-4);
}

/** One entry of the POST body: blanks left out, text trimmed, country upper-cased. */
export function toRequestDriver(draft: DriverDraft): RenterDriverFieldsInput {
  const entry: RenterDriverFieldsInput = {};

  for (const key of DRIVER_FORM_KEYS) {
    const value = draft.fields[key].trim();
    if (value === "") continue;

    entry[key] = key === "license_country" ? value.toUpperCase() : value;
  }

  return entry;
}

/** "expires before the return" -> "Expires before the return". */
export function sentence(message: string): string {
  return message.charAt(0).toUpperCase() + message.slice(1);
}

/** Server field errors for one entry, limited to the keys the form shows. */
export function fromServerErrors(
  errors: Record<string, string[]> | undefined,
): DriverFieldErrors {
  const result: DriverFieldErrors = {};
  if (!errors) return result;

  for (const key of DRIVER_FORM_KEYS) {
    const message = errors[key]?.[0];
    if (message) result[key] = sentence(message);
  }

  return result;
}
